import { observe } from "mobx";
import { events } from "@app/lib/store";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ReportDocumentSection } from "@app/constants";
import commentStore from "@app/state/store/report-document/comment";
import cn from "classnames";

export const highlightKey = new PluginKey("highlightSelection");
export const updateCommentKey = new PluginKey("updateComment");

const skipNodes = [
    "paragraph",
    "listItem",
    "orderedList",
    "bulletList",
    "table",
    "tableHeader",
    "tableRow",
    "tableCell",
];

function highlightDecorators(doc, from, to) {
    const decorations = [];

    doc.nodesBetween(from, to, (node, pos) => {
        if (node.isTextblock && node.textContent.length > 0) {
            const textFrom = Math.max(from, pos);
            const textTo = Math.min(to, pos + node.content.size);
            if (textFrom < textTo) {
                decorations.push(
                    Decoration.inline(textFrom, textTo, { class: "highlight-selection" }),
                );
            }
        } else if (node.isBlock && !node.isTextblock && !skipNodes.includes(node.type.name)) {
            const nodeFrom = pos;
            const nodeTo = pos + node.nodeSize;
            if (nodeFrom < nodeTo) {
                decorations.push(
                    Decoration.node(nodeFrom, nodeTo, { class: "highlight-selection-node" }),
                );
            }
        } else if (node.isAtom && node.isInline && !skipNodes.includes(node.type.name)) {
            const nodeFrom = pos;
            const nodeTo = pos + node.nodeSize;
            if (nodeFrom < nodeTo) {
                decorations.push(
                    Decoration.node(nodeFrom, nodeTo, {
                        class: "highlight-selection",
                    }),
                );
            }
        }
    });

    return decorations;
}

function commentDecorators(doc) {
    const decorations = [];

    doc.descendants((node, pos) => {
        if (node.isText && node.marks.length) {
            for (const mark of node.marks) {
                if (mark.type.name === ReportDocumentSection.COMMENT.MARK_NAME) {
                    const { comment: commentList } = mark.attrs;
                    const commentIds = commentList.split(",");
                    const comments = commentStore.comments.filter((comment) =>
                        commentIds.includes(comment._id),
                    );

                    if (!comments.length) {
                        continue;
                    }

                    const focused = commentIds.includes(commentStore.focusedComment);
                    const resolved = comments.find((comment) => comment.status === "resolved");

                    let visible = !!comments.length;
                    if (commentStore.commentsTab === "open") {
                        visible = !resolved;
                    } else if (commentStore.commentsTab === "resolved") {
                        visible = resolved;
                    }

                    const className = cn({
                        "comment-mark": true,
                        focused: focused,
                        hidden: !visible,
                        resolved: resolved,
                        pending: !resolved,
                    });

                    decorations.push(
                        Decoration.inline(pos, pos + node.nodeSize, {
                            class: className,
                        }),
                    );
                }
            }
        }
    });

    return decorations;
}

// Define the plugin
export const HighlightPlugin = () =>
    new Plugin({
        key: highlightKey,
        state: {
            init(plugins, state) {
                const decorations = commentDecorators(state.doc);
                return DecorationSet.create(state.doc, decorations);
            },
            apply(tr, oldValue, oldState, newState) {
                const decorations = [];
                let update = false;

                const highlightMeta = tr.getMeta(highlightKey);
                if (highlightMeta) {
                    update = true;

                    if (highlightMeta.add) {
                        const { from, to } = newState.selection;
                        const list = highlightDecorators(oldState.doc, from, to);
                        decorations.push(...list);
                    }
                }

                const commentMeta = tr.getMeta(updateCommentKey);
                if (commentMeta) {
                    update = true;

                    const list = commentDecorators(oldState.doc);
                    decorations.push(...list);
                }

                if (update) {
                    return DecorationSet.create(newState.doc, decorations);
                }

                return oldValue.map(tr.mapping, tr.doc);
            },
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
        view(view) {
            const tabCleanup = observe(commentStore, "commentsTab", (change) => {
                view.dispatch(view.state.tr.setMeta(updateCommentKey, { commentTab: true }));
            });

            let timer = null;
            function updateComment() {
                if (!timer) {
                    timer = setTimeout(() => {
                        view.dispatch(
                            view.state.tr.setMeta(updateCommentKey, { commentTab: true }),
                        );

                        clearTimeout(timer);
                    }, 10);
                }
            }

            events.on("comments.update", updateComment);
            events.on("comments.remove", updateComment);

            return {
                destroy() {
                    tabCleanup();
                    clearTimeout(timer);

                    events.off("comments.update", updateComment);
                    events.off("comments.remove", updateComment);
                },
            };
        },
    });
